import { type ChangeEvent, type RefCallback, useCallback, useRef } from 'react';

type UseFilePickerOptions = {
  accept?: string;
  onFile: (file: File) => void;
};

type UseFilePickerResult = {
  open: () => void;
  fileInputProps: {
    ref: RefCallback<HTMLInputElement>;
    type: 'file';
    accept: string | undefined;
    style: { display: 'none' };
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  };
};

export const useFilePicker = ({
  accept,
  onFile,
}: UseFilePickerOptions): UseFilePickerResult => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const onFileRef = useRef(onFile);
  onFileRef.current = onFile;

  const setRef: RefCallback<HTMLInputElement> = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
    },
    [],
  );

  const open = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileRef.current(file);
    e.target.value = '';
  }, []);

  return {
    open,
    fileInputProps: {
      ref: setRef,
      type: 'file',
      accept,
      style: { display: 'none' },
      onChange: handleChange,
    },
  };
};
